import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useBetty } from '../../context/BettyContext';
import { BettyChatMessage } from './BettyChatMessage';
import { BettyChatInput } from './BettyChatInput';
import { BettyHeader } from './BettyHeader';

const ChatContainer = styled.div<{ $isOverlay?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${props => props.$isOverlay ? '100%' : '100vh'};
  background: ${props => props.$isOverlay ? 'transparent' :
    'linear-gradient(135deg, rgba(255, 248, 240, 0.95) 0%, rgba(255, 240, 230, 0.9) 100%)'
  };
  color: #8b4513;
  position: relative;
  overflow: hidden;
  min-height: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${props => props.$isOverlay ? 'none' : `
      radial-gradient(circle at 20% 80%, rgba(255, 182, 193, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 105, 180, 0.1) 0%, transparent 50%)
    `};
    pointer-events: none;
    z-index: 1;
    opacity: ${props => props.$isOverlay ? 0 : 1};
  }
`;

const ChatMessagesContainer = styled.div<{ $isOverlay?: boolean }>`
  flex: 1;
  padding: ${props => props.$isOverlay ? '0 16px 16px 16px' : '16px'};
  overflow-y: auto;
  position: relative;
  z-index: 2;
  min-height: 0;
  display: flex;
  flex-direction: column-reverse;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 182, 193, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 105, 180, 0.6);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 105, 180, 0.8);
    }
  }
`;

const MessagesWrapper = styled.div<{ $isOverlay?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${props => props.$isOverlay ? '4px' : '12px'};
  min-height: 100%;
  justify-content: flex-end;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 20px 16px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(255, 182, 193, 0.4);
  margin-bottom: 16px;
  backdrop-filter: blur(10px);

  h2 {
    color: #8b4513;
    font-size: 1.4rem;
    margin-bottom: 12px;
    font-family: 'Georgia', serif;
    font-weight: 600;
  }

  p {
    line-height: 1.6;
    margin-bottom: 12px;
    opacity: 0.9;
    color: #8b4513;
  }

  .examples {
    margin-top: 20px;
    text-align: left;

    h3 {
      color: #a0522d;
      font-size: 1.1rem;
      margin-bottom: 10px;
    }

    ul {
      list-style: none;
      padding: 0;

      li {
        padding: 8px 12px;
        margin: 4px 0;
        background: rgba(255, 182, 193, 0.2);
        border-radius: 6px;
        border-left: 3px solid #ff69b4;
        cursor: pointer;
        transition: all 0.3s ease;
        color: #8b4513;

        &:hover {
          background: rgba(255, 182, 193, 0.4);
          transform: translateX(5px);
          box-shadow: 0 2px 8px rgba(255, 182, 193, 0.2);
        }
      }
    }
  }
`;

const ScrollToBottomButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
}) <{ visible: boolean }>`
  position: absolute;
  bottom: 100px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 105, 180, 0.9);
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(255, 105, 180, 0.3);
  
  &:hover {
    background: #ff69b4;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(255, 105, 180, 0.4);
  }
`;

const EXAMPLE_QUERIES = [
  "I need upbeat music for a car commercial",
  "Find romantic songs from the 1960s",
  "What do you have that sounds like surf rock?",
  "I'm looking for melancholy instrumental tracks",
  "Show me some jazz for a film noir scene"
];

interface BettyChatProps {
  isOverlay?: boolean;
}

export function BettyChat({ isOverlay = false }: BettyChatProps) {
  const { messages, sendMessage, clearHistory } = useBetty();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  function scrollToBottom(): void {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  function handleScroll(): void {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom && messages.length > 3);
    }
  }

  useEffect(() => {
    if (messages.length > 0) {
      // Only auto-scroll if user is near bottom or if it's a new message
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

        if (isNearBottom || messages.length === 1) {
          scrollToBottom();
        }
      }
    }
  }, [messages]);

  function handleExampleClick(query: string): void {
    sendMessage(query);
  }

  const hasMessages = messages.length > 0;

  return (
    <ChatContainer $isOverlay={isOverlay}>
      {!isOverlay && <BettyHeader onClearHistory={clearHistory} />}

      <ChatMessagesContainer
        ref={messagesContainerRef}
        onScroll={handleScroll}
        $isOverlay={isOverlay}
      >
        <MessagesWrapper $isOverlay={isOverlay}>
          {messages.map((message) => (
            <BettyChatMessage key={message.id} message={message} />
          ))}

          {!hasMessages && (
            <WelcomeMessage>
              <h2>👋 Hi, I&apos;m Betty!</h2>
              <p>
                I help find vintage tracks from Norman Blagman&apos;s catalog for your projects.
              </p>
              {!isOverlay && (
                <>
                  <p>
                    Tell me about your project, the mood you&apos;re going for, or just describe
                    what kind of music you need, and I&apos;ll find some great options for you!
                  </p>

                  <div className="examples">
                    <h3>Try asking me something like:</h3>
                    <ul>
                      {EXAMPLE_QUERIES.map((query) => (
                        <li
                          key={query}
                          onClick={() => handleExampleClick(query)}
                        >
                          &quot;{query}&quot;
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </WelcomeMessage>
          )}

          <div ref={messagesEndRef} />
        </MessagesWrapper>
      </ChatMessagesContainer>

      <ScrollToBottomButton
        visible={showScrollButton}
        onClick={scrollToBottom}
        title="Scroll to bottom"
      >
        ↓
      </ScrollToBottomButton>

      <BettyChatInput />
    </ChatContainer>
  );
}